<template>
  <div class="ibl-import-result">
    <template v-if="areErrorsPresent">
      <h4 v-if="isImportFailed">
        {{ $tt('iblImportResult.noIblsCouldBeImported') }}:
      </h4>
      <h4 v-else>{{ $tt('iblImportResult.someIblsCouldNotBeImported') }}:</h4>
      <div
        v-for="(errors, kks) in errosByKks"
        :key="kks"
        class="issues-for-kks"
      >
        <h6>{{ $tt('iblImportResult.forKks', { kks }) }}</h6>
        <ul>
          <li
            v-for="[index, error] in getVgbmLevelErrors(errors).entries()"
            :key="getIssueKey(error, index)"
          >
            <div class="flex gap-1">
              <span> {{ error.vgbm }} - {{ error.message }} </span>
              <span v-if="error.resourceType">
                ({{ translateResourceType(error.resourceType) }})</span
              >
            </div>
          </li>
        </ul>
      </div>
    </template>

    <template v-if="areWarningsPresent">
      <h4>{{ $tt('iblImportResult.warnings') }}:</h4>
      <div
        v-for="(warnings, kks) in warningsByKks"
        :key="kks"
        class="issues-for-kks"
      >
        <h6>{{ $tt('iblImportResult.forKks', { kks }) }}</h6>
        <ul>
          <li
            v-for="[index, warning] in getVgbmLevelWarnings(warnings).entries()"
            :key="getIssueKey(warning, index)"
          >
            <div class="flex gap-1">
              <span> {{ warning.vgbm }} - {{ warning.message }} </span>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import { groupBy } from 'lodash';
  import {
    IblImportErrorType,
    IblImportResult,
    IblImportWarningType,
    IIblImportError,
    IIblImportResponseDto,
    IIblImportWarning,
    MissingResourceType,
  } from 'types';
  import { defineComponent } from 'vue';

  const KKS_LEVEL_WARNINGS = [IblImportWarningType.INVALID_KKS];

  export default defineComponent({
    name: 'IblImportResult',
    props: {
      importResult: {
        type: Object as () => IIblImportResponseDto,
        required: true,
      },
    },
    computed: {
      isImportFailed(): boolean {
        return this.importResult.importResult === IblImportResult.FAILURE;
      },
      areErrorsPresent(): boolean {
        return !!this.importResult.errors.length;
      },
      areWarningsPresent(): boolean {
        return !!this.importResult.warnings.length;
      },
      warningsByKks(): Record<string, IIblImportWarning[]> {
        return groupBy(this.importResult.warnings, 'kks');
      },
      errosByKks(): Record<string, IIblImportError[]> {
        return groupBy(this.importResult.errors, 'kks');
      },
    },
    methods: {
      getIssueKey(
        issue: IIblImportError | IIblImportWarning,
        index: number,
      ): string {
        return [issue.kks, issue.vgbm, issue.type, index].join('-');
      },
      getVgbmLevelWarnings(warnings: IIblImportWarning[]): IIblImportWarning[] {
        return warnings.filter(
          (warning) => !KKS_LEVEL_WARNINGS.includes(warning.type),
        );
      },
      isKksLevelError(error: IIblImportError): boolean {
        return (
          error.type === IblImportErrorType.MISSING_RESOURCE &&
          error.resourceType === MissingResourceType.VGBK
        );
      },
      getVgbmLevelErrors(errors: IIblImportError[]): IIblImportError[] {
        return errors.filter((error) => !this.isKksLevelError(error));
      },
      translateResourceType(resourceType: string) {
        return this.$t(`iblImportResult.missingResourceErrors.${resourceType}`);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .ibl-import-result {
    .issues-for-kks {
      margin: 1rem 0;
      ul {
        list-style-type: disc;

        li {
          margin-left: 1rem;
        }
      }
    }
  }
</style>
